import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useECTranslation } from '../i18n';

/**
 * Dual-use findings panel — backed by the /findings endpoint (v_export_control_findings).
 *
 * This is the visible payoff of the normalized findings table + lookup views: instead
 * of parsing nested JSON, it renders a flat, filterable table where AL numbers already
 * carry their control-list description + EU category and countries show as names.
 */

type FindingRow = {
    finding_id: string;
    check_id: string | null;
    check_date: string | null;
    checked_at: string | null;
    component_name: string | null;
    product_name: string | null;
    component_description: string | null;
    verdict: string | null;
    al_number: string | null;
    al_description: string | null;
    al_category: string | null;
    destination: string | null;
    destination_code: string | null;
    destination_embargoed: boolean | null;
    license_required: boolean | null;
    authorisation: string | null;
    restriction_reason: string | null;
    risk_band: string | null;
    risk_score: number | null;
    dual_use_reason: string | null;
};

type FindingsResponse = {
    items: FindingRow[];
    total: number;
};

const PAGE = 100;

// Compact local date-time (e.g. "2026-07-27 13:04") from an ISO timestamp.
const fmtDateTime = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function FindingsPanel() {
    const { t } = useECTranslation();
    const [rows, setRows] = useState<FindingRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Filters (applied on submit so we don't hammer the API on every keystroke).
    const [alQuery, setAlQuery] = useState('');
    const [destQuery, setDestQuery] = useState('');
    const [applied, setApplied] = useState({ al: '', dest: '' });
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [dualUseOnly, setDualUseOnly] = useState(false);  // show all components by default
    const [page, setPage] = useState(1);  // 1-indexed; PAGE entries per page

    // Fetch the current page (PAGE entries at offset (page-1)*PAGE), replacing rows.
    const load = async (): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                dual_use_only: String(dualUseOnly), limit: String(PAGE), offset: String((page - 1) * PAGE),
            });
            if (applied.al) params.set('al_number', applied.al);
            if (applied.dest) params.set('destination', applied.dest);
            const res = await fetch(`/findings?${params.toString()}`);
            if (!res.ok) {
                if (res.status === 401) throw new Error(t('history.loginRequired'));
                const e = await res.json().catch(() => ({} as { detail?: string }));
                throw new Error(e.detail || t('history.loadFailed', { status: res.status }));
            }
            const data: FindingsResponse = await res.json();
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('common.errorOccurred'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied, dualUseOnly, page]);

    // Reset to the filters/toggle → go back to page 1 (batched with the setter so
    // the load effect fires once).
    const goToFirstAnd = (fn: () => void): void => { fn(); setPage(1); };

    const positives = useMemo(() => rows.filter((r) => r.verdict === 'Dual-use').length, [rows]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE));
    // Windowed page list: always first + last, plus ±2 around the current page,
    // with '…' gaps — e.g. 1 … 4 5 [6] 7 8 … 42.
    const pageNumbers = useMemo(() => {
        const out: (number | '…')[] = [];
        for (let p = 1; p <= totalPages; p++) {
            if (p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2)) out.push(p);
            else if (out[out.length - 1] !== '…') out.push('…');
        }
        return out;
    }, [page, totalPages]);

    // Group components that were submitted together in one check (same check_id).
    const groups = useMemo(() => {
        const map = new Map<string, FindingRow[]>();
        for (const r of rows) {
            const key = r.check_id || r.finding_id;
            const arr = map.get(key);
            if (arr) arr.push(r); else map.set(key, [r]);
        }
        return Array.from(map.values());
    }, [rows]);

    const toggle = (key: string): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Download the full PDF compliance report for one check (transaction),
    // generated by the backend /report/{check_id} endpoint.
    const downloadReport = async (checkId: string | null): Promise<void> => {
        if (!checkId) return;
        try {
            const res = await fetch(`/report/${checkId}`);
            if (!res.ok) {
                setError(res.status === 401 ? t('history.loginRequired') : t('history.loadFailed', { status: res.status }));
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `export-control-report-${checkId.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('common.errorOccurred'));
        }
    };

    const pageBtn = (active: boolean): CSSProperties => ({
        minWidth: 32, padding: '0.3rem 0.55rem', fontSize: '0.82rem', borderRadius: 4,
        border: `1px solid ${active ? '#F26F21' : '#ddd'}`,
        background: active ? '#F26F21' : '#fff', color: active ? '#fff' : '#333',
        fontWeight: active ? 700 : 400, cursor: 'pointer',
    });

    const cell: CSSProperties = { padding: '0.6rem 0.9rem', borderBottom: '1px solid #eee', verticalAlign: 'top', fontSize: '0.85rem', lineHeight: 1.45, wordBreak: 'break-word' };
    const head: CSSProperties = { ...cell, textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '2px solid #F26F21', whiteSpace: 'nowrap' };

    return (
        <div className="findings-panel" style={{ marginBottom: '2rem' }}>
            {/* Panel heading removed — the module heading + tabs already title this view. */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <input
                    value={alQuery}
                    onChange={(e) => setAlQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') goToFirstAnd(() => setApplied({ al: alQuery.trim(), dest: destQuery.trim() })); }}
                    placeholder={t('findings.filterAl')}
                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.85rem', background: '#ffffff', color: '#111111', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <input
                    value={destQuery}
                    onChange={(e) => setDestQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') goToFirstAnd(() => setApplied({ al: alQuery.trim(), dest: destQuery.trim() })); }}
                    placeholder={t('findings.filterDestination')}
                    style={{ padding: '0.35rem 0.6rem', fontSize: '0.85rem', background: '#ffffff', color: '#111111', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <button
                    onClick={() => goToFirstAnd(() => setApplied({ al: alQuery.trim(), dest: destQuery.trim() }))}
                    className="primary-btn"
                    style={{ padding: '0.35rem 0.9rem', fontSize: '0.85rem' }}
                >
                    {t('findings.apply')}
                </button>
                {(applied.al || applied.dest) && (
                    <button
                        onClick={() => goToFirstAnd(() => { setAlQuery(''); setDestQuery(''); setApplied({ al: '', dest: '' }); })}
                        style={{ padding: '0.35rem 0.9rem', fontSize: '0.85rem' }}
                    >
                        {t('findings.clear')}
                    </button>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: '#555' }}>
                    <input type="checkbox" checked={dualUseOnly} onChange={(e) => goToFirstAnd(() => setDualUseOnly(e.target.checked))} />
                    {t('findings.dualUseOnly')}
                </label>
            </div>

            {loading && <div style={{ color: '#666' }}>{t('history.loading')}</div>}
            {error && <div className="history-error" style={{ color: '#c0392b' }}>{error}</div>}

            {!loading && !error && (
                <>
                    <div style={{ color: '#666', fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                        {t('findings.count', {
                            shown: total ? `${(page - 1) * PAGE + 1}–${(page - 1) * PAGE + rows.length}` : '0',
                            total: String(total), positives: String(positives),
                        })}
                    </div>
                    {rows.length === 0 && <div style={{ color: '#666' }}>{t('findings.empty')}</div>}

                    {/* One card per check — the components submitted together in a
                        single call are grouped under their shared context. */}
                    {groups.map((g) => {
                        const ctx = g[0];
                        const key = ctx.check_id || ctx.finding_id;
                        const isOpen = expanded.has(key);
                        // The product(s) these components were extracted from — shown
                        // once in the header, not repeated on every component row.
                        const products = Array.from(new Set(g.map((r) => r.product_name).filter(Boolean)));
                        return (
                            <div
                                key={key}
                                style={{ border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: '0.9rem', overflow: 'hidden' }}
                            >
                                <div style={{ background: '#faf5f0', padding: '0.5rem 0.75rem', borderBottom: '1px solid #eee' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'center', fontSize: '0.82rem' }}>
                                        <span>🕒 <strong>{fmtDateTime(ctx.checked_at) || ctx.check_date || '—'}</strong></span>
                                        <span>📍 {ctx.destination || ctx.destination_code || '—'}
                                            {ctx.destination_embargoed && <span style={{ color: '#c0392b', fontWeight: 600 }}> ⛔</span>}
                                        </span>
                                        <span>{t('findings.col.licence')}: <strong>{ctx.license_required === true ? t('findings.licence.required')
                                            : ctx.license_required === false ? t('findings.licence.notRequired') : '—'}</strong></span>
                                        <span>{t('findings.authorisation')}: <strong>{ctx.authorisation || '—'}</strong></span>
                                        <span>{t('findings.col.risk')}: <strong>{ctx.risk_band || '—'}</strong></span>
                                        <span style={{ color: '#666' }}>{t('findings.groupCount', { n: String(g.length) })}</span>
                                        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                                            <button onClick={() => toggle(key)} style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}>
                                                {isOpen ? t('findings.hideDetails') : t('findings.view')}
                                            </button>
                                            <button onClick={() => downloadReport(key)} className="primary-btn" style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}>
                                                {t('findings.download')}
                                            </button>
                                        </span>
                                    </div>
                                    {products.length > 0 && (
                                        <div style={{ fontSize: '0.78rem', color: '#F26F21', marginTop: '0.3rem' }}>
                                            {t('findings.product')}: <strong>{products.join(', ')}</strong>
                                        </div>
                                    )}
                                </div>
                                <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                                    <colgroup>
                                        <col style={{ width: '44%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '17%' }} />
                                        <col style={{ width: '25%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={head}>{t('findings.col.component')}</th>
                                            <th style={head}>{t('findings.col.result')}</th>
                                            <th style={head}>{t('findings.col.al')}</th>
                                            <th style={head}>{t('findings.col.category')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {g.map((r) => (
                                            <Fragment key={r.finding_id}>
                                                <tr>
                                                    <td style={{ ...cell, textAlign: 'justify' }}>{r.component_name || '—'}</td>
                                                    <td style={cell}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '0.1rem 0.45rem', borderRadius: 10,
                                                            fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap',
                                                            color: r.verdict === 'Dual-use' ? '#b34700' : '#2e7d32',
                                                            background: r.verdict === 'Dual-use' ? '#fff0e6' : '#e8f5e9',
                                                        }}>
                                                            {r.verdict === 'Dual-use' ? t('findings.verdict.dualUse') : t('findings.verdict.notControlled')}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...cell, fontWeight: 600 }}>{r.al_number || '—'}</td>
                                                    <td style={cell}>{r.al_category || '—'}</td>
                                                </tr>
                                                {isOpen && (r.component_description || r.dual_use_reason) && (
                                                    <tr>
                                                        <td colSpan={4} style={{ ...cell, background: '#fcfcfc', color: '#555', fontSize: '0.78rem', textAlign: 'justify' }}>
                                                            {r.component_description && (
                                                                <div style={{ marginBottom: r.dual_use_reason ? '0.4rem' : 0 }}>
                                                                    <strong>{t('findings.description')}:</strong> {r.component_description}
                                                                </div>
                                                            )}
                                                            {r.dual_use_reason && (
                                                                <div><strong>{t('findings.reason')}:</strong> {r.dual_use_reason}</div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                                {isOpen && (
                                    <div style={{ padding: '0.5rem 0.75rem', background: '#fafafa', borderTop: '1px solid #eee', fontSize: '0.8rem', color: '#555' }}>
                                        {ctx.restriction_reason && (
                                            <div style={{ marginBottom: '0.5rem', textAlign: 'justify' }}>
                                                <strong>{t('findings.whyRestricted')}:</strong> {ctx.restriction_reason}
                                            </div>
                                        )}
                                        <span>{t('findings.riskScore')}: <strong>{ctx.risk_score ?? '—'}</strong></span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.85rem' }}>
                            <button onClick={() => setPage(page - 1)} disabled={page === 1 || loading} style={pageBtn(false)}>‹</button>
                            {pageNumbers.map((p, i) => (
                                p === '…'
                                    ? <span key={`gap${i}`} style={{ padding: '0 0.25rem', color: '#999' }}>…</span>
                                    : <button key={p} onClick={() => setPage(p)} disabled={loading} style={pageBtn(p === page)}>{p}</button>
                            ))}
                            <button onClick={() => setPage(page + 1)} disabled={page === totalPages || loading} style={pageBtn(false)}>›</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default FindingsPanel;
